import React, { useEffect, useRef } from "react";
import makeStyles from "@mui/styles/makeStyles";

const useStyles = makeStyles(() => ({
  canvas: {
    position: "absolute",
    inset: 0,
    width: "100%",
    height: "100%",
    zIndex: 0,
    pointerEvents: "none",
  },
}));

/**
 * Floating 3D food + scooter layer behind the hero. three.js is imported
 * dynamically after mount, so it never slows the homepage's first paint;
 * rendering pauses while the hero is off screen. If WebGL is unavailable
 * the layer simply doesn't appear.
 */
function HeroBackground3D() {
  const classes = useStyles();
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    let api = null;
    let stopped = false;
    let observer = null;

    const onResize = () => {
      if (!api || !canvas.parentElement) return;
      const { clientWidth, clientHeight } = canvas.parentElement;
      api.resize(clientWidth, clientHeight);
    };
    const onMove = (e) => {
      if (!api) return;
      api.setPointer(
        e.clientX / window.innerWidth - 0.5,
        e.clientY / window.innerHeight - 0.5
      );
    };

    const mobile =
      window.matchMedia && window.matchMedia("(max-width: 899.95px)").matches;

    import("../Home3D/heroScene")
      .then((mod) => {
        if (stopped || !canvasRef.current) return;
        try {
          api = mod.createHeroScene(canvas, { mobile });
        } catch (e) {
          canvas.style.display = "none";
          return;
        }
        onResize();
        window.addEventListener("resize", onResize);
        window.addEventListener("mousemove", onMove, { passive: true });
        if (typeof IntersectionObserver !== "undefined") {
          observer = new IntersectionObserver(
            ([entry]) => api && api.setVisible(entry.isIntersecting),
            { threshold: 0 }
          );
          observer.observe(canvas);
        }
      })
      .catch(() => {
        canvas.style.display = "none";
      });

    return () => {
      stopped = true;
      window.removeEventListener("resize", onResize);
      window.removeEventListener("mousemove", onMove);
      if (observer) observer.disconnect();
      if (api) api.dispose();
    };
  }, []);

  return <canvas ref={canvasRef} className={classes.canvas} aria-hidden="true" />;
}

export default HeroBackground3D;